<script lang="ts" generics="ROW, ITEM">
    import type {Table} from '@tanstack/svelte-table';
    import type {GridState} from 'shared/ui-widgets/grid/types';
    import type {PaginationData, Pagination} from 'src/features/search/search-manager/pagination';
    import {type Readable, writable} from 'svelte/store';
    import {persistentStorageLocal} from 'shared/persistent-storage';
    import {getCurrentLanguage, getLocalization} from 'src/core';
    import {getAggregationResult, type AggregationFunctionType} from './aggregation-functions';
    import {pipe} from 'core/utils';
    import {strParams} from 'shared/utils/pipes';
    import {onDestroy} from 'svelte';
    import {cleanup} from 'shared/utils/custom-utils';
    import {tooltip} from 'shared/ui-widgets/tooltip/use.tooltip';
    import {getInfiniteScrollContext} from 'shared/ui-widgets/grid/infinite-scroll.context';
    import {Divider, Flex, Spacer} from 'shared/layouts';
    import {states} from 'shared/constants';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpDropdownMenuWrapper from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuWrapper.svelte';
    import KpDropdownMenuItem from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuItem.svelte';
    import KpPluralizeText from 'shared/components/kp-pluralize-text/KpPluralizeText.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';

    export let table$: Readable<Table<ROW>>;
    export let paginationData: PaginationData<ITEM>;
    export let pagination: Pagination<ITEM>;
    export let tableState$: Readable<GridState>;

    const localize = getLocalization();
    const locale = getCurrentLanguage();

    const infiniteScrollContext = getInfiniteScrollContext();
    const infiniteScrollLoading = infiniteScrollContext.loading$;
    const selectedAggregation$ = writable<AggregationFunctionType>('sum');
    $: selectedAggregation = $selectedAggregation$;
    const persistentSelectedAggregationUnsubscribe = persistentStorageLocal(selectedAggregation$, 'grid-bottom-bar-selected-aggregation');

    $: aggregationResult = getAggregationResult($tableState$, $table$);

    onDestroy(() => {
        cleanup(persistentSelectedAggregationUnsubscribe);
    });

    const selectDisplayedAggregation = (aggregation: AggregationFunctionType) => {
        selectedAggregation$.set(aggregation);
    };

    function formatDisplayNumber(num: number): string {
        return num.toLocaleString(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    }

    function formatDisplayDate(date: string): string {
        if (!date) {
            return '-';
        }

        return new Date(date).toLocaleDateString(locale);
    }
</script>

<div class="grid-bottom-toolbar">
    <Flex direction="column">
        <small>
            Zobrazeno řádků: {paginationData.items.length}/{Math.max(0, paginationData.totalElements)}
        </small>
    </Flex>
    <Spacer direction="horizontal" size="l"/>

    <Flex gap="m">
        <!-- If infinite scroll is enabled, do not show pagination buttons -->
        {#if !infiniteScrollContext.enabled}
            {#if paginationData.hasPreviousPage}
                <KpButton isLoading={paginationData.state.previousPage === states.PROCESSING}
                          on:click={() => pagination.requestPreviousPage()}>

                    <IconedContent icon="search">
                        {pipe(localize(/* @kp-localization vysledky.NacistPredchozichX */ 'vysledky.NacistPredchozichX'), strParams(paginationData.pageSize.toString()))}
                    </IconedContent>
                </KpButton>
            {/if}

            <KpButton isLoading={paginationData.state.nextPage === states.PROCESSING}
                      isDisabled={!paginationData.hasNextPage}
                      tooltipLabel={!paginationData.hasNextPage ? 'Není dostupná další stránka' : null}
                      on:click={() => pagination.requestNextPage()}>

                <IconedContent icon="search">
                    {#if !paginationData.pageSize}
                        {localize(/* @kp-localization vysledky.NacistDalsichX */ 'vysledky.NacistDalsi')}
                    {:else}
                        {pipe(localize(/* @kp-localization vysledky.NacistDalsichX */ 'vysledky.NacistDalsichX'), strParams(paginationData.pageSize.toString()))}
                    {/if}
                </IconedContent>
            </KpButton>
        {/if}
    </Flex>

    <!-- If infinite scroll is enabled and page is loading, show loading indicator -->
    {#if infiniteScrollContext.enabled && $infiniteScrollLoading}
        <Flex direction="column" alignItems="center" justifyContent="center">
            <KpLoadingBlock size="xs"/>
        </Flex>
    {/if}

    <Spacer flex="1"/>

    <Flex direction="column">
        <small class="selected-counts">
            {aggregationResult.counts.cells}
            <KpPluralizeText count={aggregationResult.counts.cells}
                             zeroCountText="vybraných buňek"
                             oneCountText="vybraná buňka"
                             twoThreeFourCountText="vybrané buňky"
                             moreText="vybraných buňek"/>
        </small>

        <small class="selected-counts">
            {aggregationResult.counts.rows}
            <KpPluralizeText count={aggregationResult.counts.rows}
                             zeroCountText="řádků"
                             oneCountText="řádek"
                             twoThreeFourCountText="řádky"
                             moreText="řádků"/>

            ,

            {aggregationResult.counts.columns}
            <KpPluralizeText count={aggregationResult.counts.columns}
                             zeroCountText="sloupců"
                             oneCountText="sloupec"
                             twoThreeFourCountText="sloupce"
                             moreText="sloupců"/>
        </small>
    </Flex>
    <Spacer direction="horizontal" size="l"/>

    <KpDropdownMenuWrapper>
        <span slot="button">
            <span>
                {#if selectedAggregation === 'sum'}
                    Suma: {formatDisplayNumber(aggregationResult.numbers.sum)}
                {:else if selectedAggregation === 'avg'}
                    Průměr: {formatDisplayNumber(aggregationResult.numbers.avg)}
                {:else if selectedAggregation === 'min'}
                    Min: {formatDisplayNumber(aggregationResult.numbers.min)}
                {:else if selectedAggregation === 'max'}
                    Max: {formatDisplayNumber(aggregationResult.numbers.max)}
                {:else if selectedAggregation === 'date-min'}
                    Min datum: {formatDisplayDate(aggregationResult.dates.min)}
                {:else if selectedAggregation === 'date-max'}
                    Max datum: {formatDisplayDate(aggregationResult.dates.max)}
                {/if}
            </span>
        </span>

        <svelte:fragment slot="menu">
            <Spacer direction="vertical" size="xs"/>
            <small class="dropdown-heading">Číselné hodnoty ({aggregationResult.numbers.totalCount})</small>

            <KpDropdownMenuItem on:click={() => selectDisplayedAggregation('sum')}>
                Suma: {formatDisplayNumber(aggregationResult.numbers.sum)}
            </KpDropdownMenuItem>

            <KpDropdownMenuItem on:click={() => selectDisplayedAggregation('avg')}>
                Průměr: {formatDisplayNumber(aggregationResult.numbers.avg)}
            </KpDropdownMenuItem>

            <KpDropdownMenuItem on:click={() => selectDisplayedAggregation('min')}>
                Min: {formatDisplayNumber(aggregationResult.numbers.min)}
            </KpDropdownMenuItem>

            <KpDropdownMenuItem on:click={() => selectDisplayedAggregation('max')}>
                Max: {formatDisplayNumber(aggregationResult.numbers.max)}
            </KpDropdownMenuItem>

            <Spacer direction="vertical" size="s"/>
            <Divider direction="horizontal" size="100%"/>
            <Spacer direction="vertical" size="s"/>
            <small class="dropdown-heading">Datumové hodnoty ({aggregationResult.dates.totalCount})</small>

            <KpDropdownMenuItem on:click={() => selectDisplayedAggregation('date-min')}>
                Min: {formatDisplayDate(aggregationResult.dates.min)}
            </KpDropdownMenuItem>

            <KpDropdownMenuItem on:click={() => selectDisplayedAggregation('date-max')}>
                Max: {formatDisplayDate(aggregationResult.dates.max)}
            </KpDropdownMenuItem>
        </svelte:fragment>
    </KpDropdownMenuWrapper>

    {#if aggregationResult.anyErrorsInSelection}
        <Spacer direction="horizontal" size="m"/>

        <span use:tooltip={{content: 'Pozor! Ve vybraných buňkách jsou chyby.', role: 'tooltip', enabled: true}}>
            <UIcon icon="exclamation" color="var(--danger-red)"/>
        </span>
    {/if}
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .grid-bottom-toolbar {
        padding: @spacing-s @spacing-xl;
        border-top: 1px solid @themed-border-default;
        flex-shrink: 0;
        display: flex;
        align-items: center;
    }

    .dropdown-heading {
        padding-left: @spacing-m;
        padding-bottom: @spacing-s;
        padding-top: @spacing-xs;
        color: @themed-text-muted;
    }
</style>